import { SGWTConnectCore, SGWTConnectError } from "@sgwt/connect-core";
import { useState, useEffect } from "react";
import { getNavigateAs } from "./navigateAs";
import { UserType } from "./services/users.service.types";

interface SgwtConnectCorrected extends SGWTConnectCore {
  getJWTClaims: () => {
    sub?: string;
  };
}

interface ISgwtConnect extends Element {
  sgwtConnect: SgwtConnectCorrected;
}

export function getSgwtConnect(): SgwtConnectCorrected | undefined {
  return (document.querySelector("sgwt-connect") as ISgwtConnect)?.sgwtConnect;
}

const altAuthMarker = "alt-auth:";

const isAltAuth = (url: string) => url.startsWith(altAuthMarker);

export const removeUrlMetadata = (url: string) => {
  return isAltAuth(url) ? url.substring(altAuthMarker.length) : url;
};

const requestSgConnectAlternateAuthorization = () => {
  // emit event which we listen to in <SgConnectAltAuthProvider /> below

  return new Promise<string>((resolve, reject) => {
    const handleMessage = (
      e: MessageEvent<{
        type: string;
        header?: string;
        error?: SGWTConnectError;
      }>
    ) => {
      // listen for events which are received from the <iframe /> defined in <SgConnectAltAuthProvider />
      // see: login-alt-auth.html for where the message is posted
      if (
        e.data?.type !==
        alternateAuthorizationEvents.sgConnectAlternateAuthHeaderResponse
      ) {
        // not a relevant message
        return;
      }

      // we have a response, stop listening
      window.removeEventListener("message", handleMessage);
      if (e.data.header) {
        // success ✨
        resolve(e.data.header);
      } else if (e.data.error) {
        reject(e.data.error);
      }
    };

    const domElementIsReady = () =>
      !!document.getElementById(alternateAuthorizationConfig.domElementId);

    const requestSgConnectAlternateAuthHeader = () => {
      window.addEventListener("message", handleMessage);
      window.dispatchEvent(
        new CustomEvent(
          alternateAuthorizationEvents.requestSgConnectAlternateAuthHeader
        )
      );
    };

    if (domElementIsReady()) {
      requestSgConnectAlternateAuthHeader();
    } else {
      const intervalId = setInterval(() => {
        if (domElementIsReady()) {
          clearInterval(intervalId);
          requestSgConnectAlternateAuthHeader();
        }
      }, 200);
    }
  });
};

// event emitter is used because the request for an auth token is made outside of 'react'
const alternateAuthorizationEvents = {
  // bearer token requested for http or graphql request
  requestSgConnectAlternateAuthHeader: "request-alt-auth",
  // the iframe /login-alt-auth.html posts this message to the main window
  sgConnectAlternateAuthHeaderResponse: "sgconnect-auth-response",
};

export const alternateAuthorizationConfig = {
  domElementId: "alt-auth-container",
  events: alternateAuthorizationEvents,
};

export const getAuthorizationHeader = async (
  url: string,
  opts: { ignoreNavigateAs?: boolean } = {}
) => {
  if (!opts.ignoreNavigateAs && getNavigateAs() === UserType.Public) {
    return null;
  }
  const authorizationHeader = isAltAuth(url)
    ? await requestSgConnectAlternateAuthorization()
    : getSgwtConnect()?.getAuthorizationHeader();
  return authorizationHeader;
};

export const getAlternateAuthorizationHeader = async (
  url: string
): Promise<string | null> => {
  if (getNavigateAs() === UserType.Public) {
    return null;
  }
  const authorizationHeader = isAltAuth(url)
    ? getSgwtConnect()?.getAuthorizationHeader()
    : await requestSgConnectAlternateAuthorization();
  return authorizationHeader ?? null;
};

export function requestAuthentication(
  location?: Pick<Location, "search" | "pathname">,
  sgwtConnect?: SGWTConnectCore | undefined
) {
  if (!location) {
    location = window.location;
  }
  if (!sgwtConnect) {
    sgwtConnect = getSgwtConnect();
  }

  if (location.pathname.startsWith("/404")) {
    const from = new URLSearchParams(location.search).get("from");
    if (from) {
      requestAuthenticationForUrl(from);
      return;
    }
  }
  sgwtConnect?.requestAuthorization();
}

export function requestAuthenticationForUrl(
  url: string,
  sgwtConnect?: SGWTConnectCore | undefined
) {
  if (!sgwtConnect) {
    sgwtConnect = getSgwtConnect();
  }
  window.history.pushState({}, "", url);
  sgwtConnect?.requestAuthorization();
}

export const useSgConnect = () => {
  const [sgConnect, setSgConnect] = useState(getSgwtConnect());

  useEffect(() => {
    if (sgConnect) {
      return;
    }
    const setSgConnectWhenReady = () => {
      if (!sgConnect) {
        setSgConnect(getSgwtConnect());
      }
    };
    document.addEventListener("sgwt-connect--ready", setSgConnectWhenReady);
    return () => {
      document.removeEventListener(
        "sgwt-connect--ready",
        setSgConnectWhenReady
      );
    };
  }, [sgConnect]);

  return sgConnect;
};
